import React, { useState, useEffect } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FileSignature, Save, ExternalLink } from "lucide-react";
import { AGREEMENT_STATUSES } from "./meetingConstants";

export default function AgreementPanel({ meetingId, agreement }) {
  const [ag, setAg] = useState({ needed: false, agreement_type: "", professional_involved: "", status: "agreement_needed", external_signing_link: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (agreement) setAg((a) => ({ ...a, ...agreement })); }, [agreement]);

  const save = async () => {
    setSaving(true);
    try { await api.put(`/meetings/${meetingId}/agreement`, ag); toast.success("Agreement details saved."); }
    catch (e) { toast.error(errMsg(e, "Save failed.")); } finally { setSaving(false); }
  };

  return (
    <Card className="p-4 border-[#E6EDF2]" data-testid="agreement-panel">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2"><FileSignature size={14} /> Contract / agreement prep</div>
      <div className="flex items-center gap-2 mb-3">
        <Switch id="agreement-needed" checked={!!ag.needed} onCheckedChange={(v) => setAg({ ...ag, needed: v })} data-testid="agreement-needed-toggle" />
        <Label htmlFor="agreement-needed" className="text-[13px] text-[#1C4358]">An agreement is needed for this family</Label>
      </div>
      {ag.needed && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[12px] text-[#4B5563]">Agreement type</Label><Input value={ag.agreement_type || ""} onChange={(e) => setAg({ ...ag, agreement_type: e.target.value })} placeholder="e.g. Placement agreement" className="h-9 text-[14px]" /></div>
            <div><Label className="text-[12px] text-[#4B5563]">Professional / lawyer involved</Label><Input value={ag.professional_involved || ""} onChange={(e) => setAg({ ...ag, professional_involved: e.target.value })} className="h-9 text-[14px]" /></div>
          </div>
          <div>
            <Label className="text-[12px] text-[#4B5563]">Status</Label>
            <Select value={ag.status} onValueChange={(v) => setAg({ ...ag, status: v })}>
              <SelectTrigger data-testid="agreement-status-select" className="h-9 text-[14px]"><SelectValue /></SelectTrigger>
              <SelectContent>{AGREEMENT_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[12px] text-[#4B5563] flex items-center gap-1">External signing link <span className="text-[11px] text-[#9AA5B1]">(future integration — paste your e-sign URL)</span></Label>
            <div className="flex gap-2">
              <Input value={ag.external_signing_link || ""} onChange={(e) => setAg({ ...ag, external_signing_link: e.target.value })} placeholder="https://…" className="h-9 text-[14px]" data-testid="agreement-signing-link" />
              {ag.external_signing_link ? <a href={ag.external_signing_link} target="_blank" rel="noreferrer" className="inline-flex items-center px-2 rounded-md border border-[#D6E0E7] text-[#1C4358]"><ExternalLink size={15} /></a> : null}
            </div>
          </div>
          <div><Label className="text-[12px] text-[#4B5563]">Notes</Label><Textarea value={ag.notes || ""} onChange={(e) => setAg({ ...ag, notes: e.target.value })} rows={2} className="text-[14px]" /></div>
          <p className="text-[11px] text-[#9AA5B1]">No e-signature is performed here. Signing happens externally; mark "Signed externally" or "Completed" only when it truly is.</p>
        </div>
      )}
      <Button size="sm" data-testid="agreement-save" onClick={save} disabled={saving} className="mt-3 bg-[#1C4358] hover:bg-[#163545] text-white gap-1"><Save size={14} /> Save agreement details</Button>
    </Card>
  );
}
